import React from 'react';
import PageCard from '@jg/page-card';
import Alert from '@jg/alert';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PageView, Button } from '@jg/react-tracking';
import { Helmet } from 'react-helmet';
import { RouteProps, withRouter } from 'react-router';

import { IAppState } from '../../types/state.d';
import { IJoin, IJoinMapStateToProps, IJoinDispatchToProps } from './Join.d';
import { ROUTES } from '../../consts';
import config from '../../config';
import {
  getTeamName,
  getHeroImage,
  getCharity,
  getEventName,
  getEventExpiryDate,
  getShortName,
  getTeamData,
  getNumberOfMembersFromTeamData,
  getSlicedMembersListFromTeamData,
} from '../../selectors/apiSelectors';
import { appThunk as appThunkAction, httpActions } from '../../actions';
import { makePhotoUrl, hasEventDateExpired } from '../../utils';
import { getJoinRoutes } from '../../selectors/routeSelectors';
import { getLocks } from '../../selectors/locksSelectors';

export const Join = ({
  heroImage,
  teamName,
  charityData,
  eventName,
  shortName,
  membersList,
  numberOfMembers,
  pushToPage,
  eventExpiryDate,
  joinRoutes,
  teamlocks: { charity: isCharityLocked },
}: IJoin) => {
  const { t } = useTranslation();
  const coverPhoto = makePhotoUrl(heroImage || config.defaultCoverPhoto);

  const hasEventExpired = hasEventDateExpired(eventExpiryDate);
  return (
    <PageView
      trackingData={{
        subtype: 'join team',
        event_value: 'join the team',
        page_section: 'join team > join the team',
      }}
    >
      <Helmet title={t('title.join')} />
      <main
        data-qa="join-page"
        className="jg-space-ptmd jg-space-pbxl jg-space-pbml@md"
      >
        <div className="jg-text--center jg-space-mbmd">
          <h1 className="jg-space-mbxs">
            {t('content.join.landingpage.heading')}
          </h1>
          <p className="jg-text--large">
            {t('content.join.landingpage.subtext')}
          </p>
        </div>
        <PageCard
          coverPhoto={coverPhoto}
          pageName={teamName}
          charityName={
            isCharityLocked
              ? charityData.name
              : t(['content.yourTeamPage.multiCharities', 'multiple charities'])
          }
          usersList={membersList}
          usersNumber={numberOfMembers}
          eventName={eventName}
          fundraisingForLabel={t('content.yourteampage.fundraisingfor')}
          userLabel={t('content.yourteampage.member')}
          usersLabel={t('content.yourteampage.members')}
          eventLabel={t('content.yourteampage.event')}
          link={`${config.basePageUri}${shortName}`}
        />
        <div className="row">
          <div className="col-md-8 offset-md-2 jg-text--center">
            <Button
              data-qa="create-fundraising-button"
              type="button"
              disabled={hasEventExpired}
              onClick={() =>
                joinRoutes.nextStep && pushToPage(joinRoutes.nextStep)
              }
              className="jg-btn jg-space-mtmd"
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                event: 'click',
                event_value: 'create fundraising page',
                page_section: 'join team > join the team',
              }}
            >
              {t('content.join.button.createfundraisingpage')}
            </Button>
            <p className="jg-space-mtml jg-text--large">
              {t('content.join.landingpage.connect.subtext')}
            </p>
            <Button
              data-qa="link-existing-button"
              type="button"
              onClick={() => pushToPage(ROUTES.LINK_FUNDRAISING_PAGE)}
              className="jg-btn"
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                event: 'click',
                event_value: 'link existing fundraising page',
                page_section: 'join team > join the team',
              }}
            >
              {t('content.join.landingpage.button.linkexisting')}
            </Button>
          </div>
        </div>
        {hasEventExpired && (
          <Alert
            type="info"
            displayType="inline"
            className="jg-text--pull-left jg-space-mtms"
            data-qa="expired-event-informational-alert"
          >
            {t('content.informational.event.expired')}
          </Alert>
        )}
      </main>
    </PageView>
  );
};

export const mapDispatchToProps: IJoinDispatchToProps = {
  pushToPage: appThunkAction.pushToPage,
  resetRequestError: httpActions.resetRequestError,
};

export const mapStateToProps = (
  state: IAppState,
  ownProps: RouteProps,
): IJoinMapStateToProps => ({
  teamName: getTeamName(state),
  heroImage: getHeroImage(state),
  charityData: getCharity(state),
  eventName: getEventName(state),
  shortName: getShortName(state),
  teamData: getTeamData(state),
  eventExpiryDate: getEventExpiryDate(state),
  membersList: getSlicedMembersListFromTeamData(3)(state),
  numberOfMembers: getNumberOfMembersFromTeamData(state),
  joinRoutes: getJoinRoutes(state, ownProps),
  teamlocks: getLocks(state),
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(Join),
);
